import React, { useState, useEffect } from 'react';
import api from '../../config/api';
import { useWebSocket } from '../../config/websocket';
import { 
  FaSpinner, 
  FaEye, 
  FaUsers,
  FaPercentage,
  FaCalendarAlt,
  FaExclamationTriangle,
  FaThumbtack,
  FaPaperPlane,
  FaChevronRight
} from 'react-icons/fa';

// --- SVG Icon Components ---
const SendIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13"></line>
    <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
  </svg>
);

const SuccessIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
    <polyline points="22 4 12 14.01 9 11.01"></polyline>
  </svg>
);

const ErrorIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="12" y1="8" x2="12" y2="12"></line>
    <line x1="12" y1="16" x2="12.01" y2="16"></line>
  </svg>
);

// --- Main Broadcast Component ---
const BroadcastMessage = () => {
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    priority: 'high',
    is_pinned: true
  });
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  
  // New state for admin announcements list
  const [adminAnnouncements, setAdminAnnouncements] = useState([]);
  const [loadingAnnouncements, setLoadingAnnouncements] = useState(true);
  const [announcementsError, setAnnouncementsError] = useState(null);
  
  // Get user info for WebSocket
  const jwtToken = localStorage.getItem('token') || sessionStorage.getItem('token');
  const currentUserId = localStorage.getItem('current_user_id') || sessionStorage.getItem('current_user_id');

  // Initialize WebSocket
  const { 
    initialize, 
    subscribeToAnnouncementsChannel 
  } = useWebSocket(jwtToken, currentUserId);

  // Setup WebSocket for admin notifications
  useEffect(() => {
    if (jwtToken && currentUserId) {
      // Initialize WebSocket connection
      initialize();
      
      // Subscribe to admin announcements channel for feedback
      const unsubscribe = subscribeToAnnouncementsChannel(
        // onBroadcastSent callback
        (data) => {
          console.log('Broadcast sent successfully via WebSocket:', data);
          showNotification('success', 'Broadcast sent successfully!', 
            `Announcement is being delivered to ${data.total_recipients || 'all'} members.`);
          // Refresh the admin announcements list
          fetchAdminAnnouncements();
        },
        // onBroadcastFailed callback
        (error) => {
          console.error('Broadcast failed via WebSocket:', error);
          showNotification('error', 'Broadcast failed', 
            error.message || 'There was an error sending the broadcast.');
        }
      );
      
      return () => {
        if (unsubscribe) {
          // Cleanup handled by the hook
        }
      };
    }
  }, [jwtToken, currentUserId, initialize, subscribeToAnnouncementsChannel]);

  // Fetch admin announcements on component mount
  useEffect(() => {
    fetchAdminAnnouncements();
  }, []);

  const fetchAdminAnnouncements = async () => {
    setLoadingAnnouncements(true);
    setAnnouncementsError(null);
    
    try {
      const response = await api.get('/announcements/admin/list');
      
      if (response.data.success && response.data.data) {
        setAdminAnnouncements(response.data.data.announcements || []);
      } else {
        setAnnouncementsError(response.data.message || 'Failed to load announcements');
      }
    } catch (error) {
      setAnnouncementsError(error.response?.data?.message || 'Failed to load. Please try again.');
      console.error('Error fetching admin announcements:', error);
    } finally {
      setLoadingAnnouncements(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const showNotification = (type, title, message) => {
    setNotification({ type, title, message });
    
    // Auto-hide notification after 5 seconds
    setTimeout(() => {
      setNotification(null);
    }, 5000);
  };

  const handleSendBroadcast = async () => {
    // Validate required fields
    if (!formData.title.trim()) {
      showNotification('error', 'Validation Error', 'Please enter a title for the announcement.');
      return;
    }
    
    if (!formData.content.trim()) {
      showNotification('error', 'Validation Error', 'Please enter the announcement content.');
      return;
    }

    setIsLoading(true);

    try {
      // Prepare the data according to Postman format
      const requestData = {
        title: formData.title,
        content: formData.content,
        priority: formData.priority,
        is_pinned: formData.is_pinned
      };

      console.log('Sending broadcast with data:', requestData);

      // Send the broadcast
      const response = await api.post('/announcements', requestData);
      
      if (response.data.success) {
        const stats = response.data.data?.stats;
        const announcement = response.data.data?.announcement;
        
        showNotification('success', 'Broadcast Sent!', 
          `Your announcement is being delivered to ${stats?.total_recipients || 'all eligible'} members.`);
        
        // Reset form
        setFormData({
          title: '',
          content: '',
          priority: 'high',
          is_pinned: true
        });
        
        // Refresh admin announcements list
        fetchAdminAnnouncements();
        
        // Log announcement ID for tracking
        if (announcement?.id) {
          console.log(`Announcement ID: ${announcement.id}`);
        }
      } else {
        showNotification('error', 'Failed to Send', 
          response.data.message || 'Unknown error occurred while sending broadcast.');
      }
    } catch (error) {
      console.error('Error sending broadcast:', error);
      
      // Detailed error handling
      let errorMessage = 'Failed to send broadcast. Please try again.';
      
      if (error.response) {
        // Server responded with error status
        if (error.response.status === 401) {
          errorMessage = 'Your session has expired. Please login again.';
        } else if (error.response.status === 403) {
          errorMessage = 'You do not have permission to send broadcasts.';
        } else if (error.response.status === 422) {
          errorMessage = 'Validation error. Please check your inputs.';
          if (error.response.data?.errors) {
            const errors = Object.values(error.response.data.errors).flat();
            errorMessage = errors.join(', ');
          }
        } else if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.request) {
        errorMessage = 'No response from server. Please check your connection.';
      }
      
      showNotification('error', 'Send Failed', errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'urgent': return '#dc2626';
      case 'high': return '#ef4444';
      case 'normal': return '#f59e0b';
      case 'low': return '#10b981';
      default: return '#6b7280';
    }
  };

  const getReadPercentage = (readCount, totalRecipients) => {
    if (!totalRecipients || totalRecipients === 0) return 0;
    return Math.round((readCount / totalRecipients) * 100);
  };

  return (
    <>
      <style>{`
        .broadcast-page {
          min-height: 100vh;
          background: white;
          padding: 1rem;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif;
          width: 100%;
          overflow-x: hidden;
        }
        
        .broadcast-container {
          display: flex;
          gap: 2rem;
          max-width: 1400px;
          margin: 0 auto;
          width: 100%;
        }
        
        .broadcast-form-section {
          flex: 1;
          max-width: 800px;
        }
        
        /* SIDEBAR STYLES - UNIQUE CLASS NAMES */
        .announcements-history-panel {
          flex: 0 0 500px;
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          overflow: hidden;
          border: 1px solid #e5e7eb;
        }
        
        .history-panel-header {
          padding: 1.5rem;
          background: #0055cc;
          color: white;
        }
        
        .history-panel-title {
          font-size: 1.25rem;
          font-weight: 600;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: white;
        }
        
        .history-panel-subtitle {
          font-size: 0.875rem;
          opacity: 0.9;
          margin-top: 0.25rem;
          color: rgba(255, 255, 255, 0.9);
        }
        
        .history-list-container {
          padding: 1.5rem;
          max-height: 70vh;
          overflow-y: auto;
        }
        
        .history-item-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 10px;
          padding: 1.25rem;
          margin-bottom: 1rem;
          transition: all 0.2s;
          cursor: pointer;
        }
        
        .history-item-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 85, 204, 0.1);
          border-color: #0055cc;
        }
        
        .history-card-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 0.75rem;
        }
        
        .history-card-title {
          font-size: 1rem;
          font-weight: 600;
          color: #1f2937;
          margin: 0;
          flex: 1;
        }
        
        .history-card-priority {
          padding: 0.25rem 0.75rem;
          border-radius: 9999px;
          font-size: 0.75rem;
          font-weight: 500;
          color: white;
        }
        
        .history-card-content {
          color: #4b5563;
          font-size: 0.875rem;
          line-height: 1.5;
          margin-bottom: 1rem;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        
        .history-card-stats {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 0.75rem;
          margin-bottom: 0.75rem;
        }
        
        .history-stat-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 0.5rem;
          background: #f9fafb;
          border-radius: 6px;
          border: 1px solid #e5e7eb;
        }
        
        .history-stat-label {
          font-size: 0.75rem;
          color: #6b7280;
          margin-bottom: 0.25rem;
        }
        
        .history-stat-value {
          font-size: 0.875rem;
          font-weight: 600;
          color: #111827;
        }
        
        .history-card-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 0.75rem;
          color: #6b7280;
        }
        
        .history-card-date {
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }
        
        .history-pinned-badge {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          color: #0055cc;
        }
        
        .history-empty-state {
          text-align: center;
          padding: 3rem 1rem;
          color: #6b7280;
        }
        
        .history-empty-icon {
          font-size: 3rem;
          margin-bottom: 1rem;
          opacity: 0.5;
        }
        
        .history-loading-state {
          text-align: center;
          padding: 3rem 1rem;
        }
        
        .history-spinner {
          animation: spin 1s linear infinite;
          color: #0055cc;
        }
        
        /* Keep existing styles below (from your original component) - UPDATED TO USE #0055cc */
        
        .send-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          background-color: #0055cc;
          color: white;
          padding: 12px 32px;
          border-radius: 6px;
          border: none;
          font-size: 16px;
          font-weight: 500;
          cursor: pointer;
          width: 100%;
          transition: all 0.2s;
          position: relative;
          overflow: hidden;
        }
        
        .send-button:hover:not(:disabled) {
          background-color: #0044aa;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 85, 204, 0.3);
        }
        
        .send-button:disabled {
          background-color: #93C5FD;
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
        }
        
        .send-button:active:not(:disabled) {
          transform: translateY(0);
        }
        
        .options-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
          margin-top: 1rem;
        }
        
        .option-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        
        .priority-select, .pin-toggle {
          padding: 10px 12px;
          border: 1px solid #D1D5DB;
          border-radius: 6px;
          font-size: 14px;
          background-color: white;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .priority-select:hover, .pin-toggle:hover {
          border-color: #0055cc;
        }
        
        .priority-select:focus, .pin-toggle:focus {
          outline: none;
          border-color: #0055cc;
          box-shadow: 0 0 0 3px rgba(0, 85, 204, 0.1);
        }
        
        .pin-toggle {
          display: flex;
          align-items: center;
          justify-content: space-between;
          cursor: pointer;
          padding: 10px 12px;
        }
        
        .pin-toggle-label {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          font-size: 14px;
          color: #374151;
        }
        
        .toggle-switch {
          position: relative;
          display: inline-block;
          width: 50px;
          height: 24px;
        }
        
        .toggle-switch input {
          opacity: 0;
          width: 0;
          height: 0;
        }
        
        .toggle-slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: #ccc;
          transition: .4s;
          border-radius: 24px;
        }
        
        .toggle-slider:before {
          position: absolute;
          content: "";
          height: 16px;
          width: 16px;
          left: 4px;
          bottom: 4px;
          background-color: white;
          transition: .4s;
          border-radius: 50%;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
        
        input:checked + .toggle-slider {
          background-color: #0055cc;
        }
        
        input:checked + .toggle-slider:before {
          transform: translateX(26px);
        }
        
        /* Notification Styles - UPDATED TO USE #0055cc */
        .notification-container {
          position: fixed;
          top: 20px;
          right: 20px;
          z-index: 1000;
          max-width: 400px;
          animation: slideIn 0.3s ease-out;
        }
        
        .notification {
          padding: 16px 20px;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          margin-bottom: 12px;
          display: flex;
          align-items: flex-start;
          gap: 12px;
          transform-origin: top right;
          background-color: white;
          border-left: 4px solid #0055cc;
        }
        
        .notification.success {
          border-left-color: #0055cc;
        }
        
        .notification.error {
          border-left-color: #dc2626;
        }
        
        .notification-icon {
          flex-shrink: 0;
          margin-top: 2px;
          color: #0055cc;
        }
        
        .notification-content {
          flex: 1;
        }
        
        .notification-title {
          font-weight: 600;
          font-size: 14px;
          margin: 0 0 4px 0;
          color: #1f2937;
        }
        
        .notification.success .notification-title {
          color: #0055cc;
        }
        
        .notification.error .notification-title {
          color: #7F1D1D;
        }
        
        .notification-message {
          font-size: 13px;
          line-height: 1.4;
          margin: 0;
          color: #4b5563;
        }
        
        .notification.success .notification-message {
          color: #0055cc;
        }
        
        .notification.error .notification-message {
          color: #B91C1C;
        }
        
        .notification-close {
          background: none;
          border: none;
          padding: 4px;
          cursor: pointer;
          color: #6B7280;
          font-size: 18px;
          line-height: 1;
          margin-left: 8px;
        }
        
        .notification-close:hover {
          color: #374151;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        /* Form field focus effects - UPDATED TO USE #0055cc */
        .form-group-card:focus-within {
          box-shadow: 0 0 0 3px rgba(0, 85, 204, 0.1);
          border-color: #0055cc;
        }
        
        input:focus, textarea:focus {
          outline: none;
          border-color: #0055cc;
          box-shadow: 0 0 0 3px rgba(0, 85, 204, 0.1);
        }
        
        /* Mobile Styles */
        @media (max-width: 1200px) {
          .broadcast-container {
            flex-direction: column;
          }
          
          .announcements-history-panel {
            flex: none;
            width: 100%;
            max-width: 800px;
            margin: 0 auto;
          }
        }
        
        @media (max-width: 768px) {
          .broadcast-page {
            padding: 0.75rem;
          }
          
          .options-grid {
            grid-template-columns: 1fr;
            gap: 0.75rem;
          }
          
          .notification-container {
            top: 10px;
            right: 10px;
            left: 10px;
            max-width: none;
          }
          
          .history-card-stats {
            grid-template-columns: 1fr;
          }
        }
        
        @media (max-width: 480px) {
          .broadcast-page {
            padding: 0.5rem;
          }
          
          .form-group-card {
            padding: 1rem;
          }
          
          .options-section {
            padding: 1rem;
          }
          
          .history-list-container {
            padding: 1rem;
          }
        }
      `}</style>
      
      {/* Notification Container */}
      {notification && (
        <div className="notification-container">
          <div className={`notification ${notification.type}`}>
            <div className="notification-icon">
              {notification.type === 'success' ? <SuccessIcon /> : <ErrorIcon />}
            </div>
            <div className="notification-content">
              <h4 className="notification-title">{notification.title}</h4>
              <p className="notification-message">{notification.message}</p>
            </div>
            <button 
              className="notification-close"
              onClick={() => setNotification(null)}
            >
              ×
            </button>
          </div>
        </div>
      )}
      
      <div className="broadcast-page">
        <div className="broadcast-container">
          {/* Left Side - Broadcast Form */}
          <div className="broadcast-form-section">
            <div style={styles.header}>
              <h1 style={styles.h1}>Send Announcement</h1>
              <p style={styles.subtitle}>Create and send announcements to members</p>
            </div>
            
            <div style={{marginTop: '2rem'}}>
              {/* Message Title */}
              <div style={styles.formGroup} className="form-group-card">
                  <label style={styles.label} htmlFor="title">Message Title <span style={{color: '#EF4444'}}>*</span></label>
                  <input 
                    id="title" 
                    name="title"
                    type="text" 
                    value={formData.title} 
                    onChange={handleInputChange} 
                    style={styles.textInput} 
                    placeholder="Enter message title..."
                    disabled={isLoading}
                    maxLength="255"
                  />
                  <div style={{fontSize: '12px', color: '#6B7280', marginTop: '4px'}}>
                    {formData.title.length}/255 characters
                  </div>
              </div>

              {/* Message Content */}
              <div style={styles.formGroup} className="form-group-card">
                  <label style={styles.label} htmlFor="content">Message Content <span style={{color: '#EF4444'}}>*</span></label>
                  <textarea 
                    id="content" 
                    name="content"
                    value={formData.content} 
                    onChange={handleInputChange} 
                    style={styles.textareaInput} 
                    placeholder="Enter your announcement message..."
                    disabled={isLoading}
                    rows="8"
                    maxLength="5000"
                  ></textarea>
                  <div style={{fontSize: '12px', color: '#6B7280', marginTop: '4px'}}>
                    {formData.content.length}/5000 characters
                  </div>
              </div>

              {/* Priority and Pin Options */}
              <div style={styles.optionsSection} className="options-section">
                <h3 style={styles.optionsTitle}>Message Options</h3>
                <div className="options-grid">
                  <div style={styles.optionGroup}>
                    <label style={styles.optionLabel}>Priority</label>
                    <select 
                      name="priority"
                      value={formData.priority} 
                      onChange={handleInputChange}
                      className="priority-select"
                      disabled={isLoading}
                    >
                      <option value="low">Low</option>
                      <option value="normal">Normal</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                    <div style={{fontSize: '11px', color: '#6B7280'}}>
                      {formData.priority === 'urgent' && 'Members will receive urgent notifications'}
                      {formData.priority === 'high' && 'Important announcements for immediate attention'}
                      {formData.priority === 'normal' && 'Regular announcements'}
                      {formData.priority === 'low' && 'Informational messages'}
                    </div>
                  </div>
                  
                  <div style={styles.optionGroup}>
                    <label style={styles.optionLabel}>Pin Message</label>
                    <div className="pin-toggle">
                      <label className="pin-toggle-label">
                        <span>Pin to top</span>
                        <div className="toggle-switch">
                          <input 
                            type="checkbox" 
                            name="is_pinned"
                            checked={formData.is_pinned}
                            onChange={handleInputChange}
                            disabled={isLoading}
                          />
                          <span className="toggle-slider"></span>
                        </div>
                      </label>
                    </div>
                    <div style={{fontSize: '11px', color: '#6B7280'}}>
                      Pinned announcements stay at the top of the list
                    </div>
                  </div>
                </div>
              </div>

              {/* Send Button */}
              <div style={{marginTop: '2rem'}}>
                <button 
                  className="send-button" 
                  onClick={handleSendBroadcast}
                  disabled={isLoading || !formData.title.trim() || !formData.content.trim()}
                >
                  {isLoading ? (
                    <>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid white',
                        borderTop: '2px solid transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <SendIcon />
                      Send Broadcast
                    </>
                  )}
                </button>
                
                {(!formData.title.trim() || !formData.content.trim()) && (
                  <div style={{
                    fontSize: '12px',
                    color: '#EF4444',
                    marginTop: '8px',
                    textAlign: 'center'
                  }}>
                    * Fill in all required fields to enable sending
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Right Side - Admin Announcements List with unique class names */}
          <div className="announcements-history-panel">
            <div className="history-panel-header">
              <h2 className="history-panel-title">
                <FaPaperPlane /> Your Announcment
              </h2>
              <p className="history-panel-subtitle">
                View all announcements you've sent
              </p>
            </div>
            
            <div className="history-list-container">
              {loadingAnnouncements ? (
                <div className="history-loading-state">
                  <FaSpinner className="history-spinner" />
                  <p>Loading your announcements...</p>
                </div>
              ) : announcementsError ? (
                <div className="history-empty-state">
                  <div className="history-empty-icon">⚠️</div>
                  <p>{announcementsError}</p>
                  <button 
                    onClick={fetchAdminAnnouncements}
                    style={{
                      marginTop: '1rem',
                      padding: '0.5rem 1rem',
                      backgroundColor: '#0055cc',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Retry
                  </button>
                </div>
              ) : adminAnnouncements.length === 0 ? (
                <div className="history-empty-state">
                  <div className="history-empty-icon">📢</div>
                  <h3>No announcements yet</h3>
                  <p>Send your first broadcast to see it here</p>
                </div>
              ) : (
                adminAnnouncements.map((announcement) => (
                  <div key={announcement.id} className="history-item-card">
                    <div className="history-card-header">
                      <h3 className="history-card-title">{announcement.title}</h3>
                      <div 
                        className="history-card-priority"
                        style={{ backgroundColor: getPriorityColor(announcement.priority) }}
                      >
                        {announcement.priority}
                      </div>
                    </div>
                    
                    <p className="history-card-content">{announcement.content}</p>
                    
                    <div className="history-card-stats">
                      <div className="history-stat-item">
                        <span className="history-stat-label">Recipients</span>
                        <span className="history-stat-value">
                          <FaUsers style={{ marginRight: '0.25rem', color: '#0055cc' }} />
                          {announcement.total_recipients || 0}
                        </span>
                      </div>
                      
                      <div className="history-stat-item">
                        <span className="history-stat-label">Delivered</span>
                        <span className="history-stat-value">
                          <FaPaperPlane style={{ marginRight: '0.25rem', color: '#0055cc' }} />
                          {announcement.delivered_count || 0}
                        </span>
                      </div>
                      
                      <div className="history-stat-item">
                        <span className="history-stat-label">Read</span>
                        <span className="history-stat-value">
                          <FaEye style={{ marginRight: '0.25rem', color: '#0055cc' }} />
                          {announcement.read_count || 0}
                        </span>
                      </div>
                    </div>
                    
                    <div className="history-card-footer">
                      <div className="history-card-date">
                        <FaCalendarAlt style={{ marginRight: '0.25rem', color: '#0055cc' }} />
                        {formatDate(announcement.created_at)}
                      </div>
                      
                      <div className="read-percentage">
                        <FaPercentage style={{ marginRight: '0.25rem', color: '#0055cc' }} />
                        {getReadPercentage(announcement.read_count || 0, announcement.total_recipients || 0)}% read
                      </div>
                      
                      {announcement.is_pinned && (
                        <div className="history-pinned-badge">
                          <FaThumbtack />
                          Pinned
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// --- Internal CSS Styles Object - UPDATED TO USE #0055cc ---
const styles = {
    header: {
        padding: '0 0.5rem',
    },
    h1: {
        fontSize: '24px',
        fontWeight: 'bold',
        color: '#0055cc',
        margin: '0 0 8px 0',
        lineHeight: '1.3',
    },
    subtitle: {
        color: '#6B7280',
        fontSize: '14px',
        margin: '0',
        lineHeight: '1.5',
    },
    formGroup: {
        backgroundColor: 'white',
        padding: '1.5rem',
        borderRadius: '8px',
        border: '1px solid #E5E7EB',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        marginBottom: '1.5rem',
        transition: 'all 0.2s',
    },
    label: {
        display: 'block',
        fontSize: '14px',
        fontWeight: '500',
        color: '#374151',
        marginBottom: '8px',
    },
    textInput: {
        width: '100%',
        padding: '12px',
        border: '1px solid #D1D5DB',
        borderRadius: '6px',
        fontSize: '14px',
        boxSizing: 'border-box',
        transition: 'all 0.2s',
    },
    textareaInput: {
        width: '100%',
        padding: '12px',
        border: '1px solid #D1D5DB',
        borderRadius: '6px',
        fontSize: '14px',
        resize: 'vertical',
        boxSizing: 'border-box',
        fontFamily: 'inherit',
        transition: 'all 0.2s',
    },
    // Options Section
    optionsSection: {
        backgroundColor: 'white',
        padding: '1.5rem',
        borderRadius: '8px',
        border: '1px solid #E2E8F0',
        marginBottom: '1.5rem',
    },
    optionsTitle: {
        fontSize: '16px',
        fontWeight: '600',
        color: '#0055cc',
        margin: '0 0 1rem 0',
    },
    optionLabel: {
        fontSize: '12px',
        fontWeight: '500',
        color: '#475569',
        marginBottom: '6px',
    }
};

export default BroadcastMessage;